import { isContactTokenFormat, isReservedAppPathSegment } from './contactTokenFormat'
import {
  defaultExperience,
  experienceWhenShellAuthUnavailable,
  iframeAppRouteUrlForExperience,
  iframeExpiredUrlForExperience,
  iframeUrlForExperience,
} from './config'
import {
  resolveShellExperience,
  type Dev2222Mode,
  type Experience,
} from './experience'
import { writePersistedExperienceForToken } from './persistedExperience'
import { publishShellAuth } from './shellAuthBridge'
import { resolveShellContactAuth, type ShellContactAuthResult } from './shellContactAuth'

export type ShellEmbedTarget =
  | { kind: 'contact-token'; token: string }
  | { kind: 'expired-page' }
  | { kind: 'app-route'; segment: string }

export type ResolvedShellEmbed = {
  experience: Experience
  target: ShellEmbedTarget
  jwtPayload: Record<string, unknown> | null
  jwtPreview: string
  smartAuth?: string
  notice?: string
}

export function shellEmbedIframeSrc(resolved: ResolvedShellEmbed): string {
  if (resolved.target.kind === 'expired-page') {
    return iframeExpiredUrlForExperience(resolved.experience)
  }
  if (resolved.target.kind === 'app-route') {
    return iframeAppRouteUrlForExperience(resolved.experience, resolved.target.segment)
  }
  return iframeUrlForExperience(resolved.experience, resolved.target.token)
}

function resolvedExpiredEmbed(pathExperience?: Experience): ResolvedShellEmbed {
  return {
    experience: experienceWhenShellAuthUnavailable(pathExperience),
    target: { kind: 'expired-page' },
    jwtPayload: null,
    jwtPreview: '{}',
  }
}

function resolvedErrorEmbed(pathExperience?: Experience): ResolvedShellEmbed {
  return {
    experience: experienceWhenShellAuthUnavailable(pathExperience),
    target: { kind: 'app-route', segment: 'error' },
    jwtPayload: null,
    jwtPreview: '{}',
  }
}

export function resolveShellEmbedForAppRoute(
  segment: string,
  pathExperience?: Experience,
): ResolvedShellEmbed {
  const trimmed = segment.trim()
  const experience = experienceWhenShellAuthUnavailable(pathExperience)
  if (trimmed === 'expired') {
    return {
      experience,
      target: { kind: 'expired-page' },
      jwtPayload: null,
      jwtPreview: '{}',
    }
  }
  if (isReservedAppPathSegment(trimmed)) {
    return {
      experience,
      target: { kind: 'app-route', segment: trimmed },
      jwtPayload: null,
      jwtPreview: '{}',
    }
  }
  return resolvedExpiredEmbed(pathExperience)
}

export async function resolveShellEmbedForPathSegment(
  segment: string,
  pathExperience: Experience | undefined,
  dev2222Mode: Dev2222Mode,
): Promise<ResolvedShellEmbed> {
  const trimmed = segment.trim()
  if (!isContactTokenFormat(trimmed)) {
    return resolveShellEmbedForAppRoute(trimmed, pathExperience)
  }
  return resolveShellEmbedForContactToken(trimmed, pathExperience, dev2222Mode)
}

export function resolveShellEmbedWithoutToken(pathExperience?: Experience): ResolvedShellEmbed {
  return resolvedErrorEmbed(pathExperience)
}

function resolveEmbedFailureExperience(
  auth: Extract<ShellContactAuthResult, { kind: 'expired' | 'auth-failed' }>,
  pathExperience: Experience | undefined,
  dev2222Mode: Dev2222Mode,
  token: string,
): Experience {
  if (auth.kind === 'expired') {
    return resolveShellExperience(auth.jwtPayload, pathExperience, dev2222Mode, defaultExperience)
  }
  return experienceWhenShellAuthUnavailable(pathExperience, token)
}

export async function resolveShellEmbedForContactToken(
  token: string,
  pathExperience: Experience | undefined,
  dev2222Mode: Dev2222Mode,
): Promise<ResolvedShellEmbed> {
  const trimmed = token.trim()
  if (!isContactTokenFormat(trimmed)) {
    return resolveShellEmbedForAppRoute(trimmed, pathExperience)
  }
  const auth = await resolveShellContactAuth(trimmed)

  if (auth.kind === 'ready') {
    const experience = resolveShellExperience(
      auth.jwtPayload,
      pathExperience,
      dev2222Mode,
      defaultExperience,
    )
    publishShellAuth(auth.smartAuth, trimmed, auth.jwtPayload, experience)
    writePersistedExperienceForToken(trimmed, experience)
    return {
      experience,
      target: { kind: 'contact-token', token: trimmed },
      jwtPayload: auth.jwtPayload,
      jwtPreview: auth.jwtPreview,
      smartAuth: auth.smartAuth,
    }
  }

  if (auth.kind === 'expired' || auth.kind === 'auth-failed') {
    return {
      experience: resolveEmbedFailureExperience(auth, pathExperience, dev2222Mode, trimmed),
      target: { kind: 'expired-page' },
      jwtPayload: null,
      jwtPreview: '{}',
    }
  }

  const experience = experienceWhenShellAuthUnavailable(pathExperience, trimmed)
  return {
    experience,
    target: { kind: 'contact-token', token: trimmed },
    jwtPayload: null,
    jwtPreview: '{}',
    notice: auth.message,
  }
}
